import React from 'react';
import { Box, IconButton, LinearProgress, Paper, Tooltip, Typography, useTheme } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import MoreVertRounded from '@mui/icons-material/MoreVertRounded';
import { useTranslation } from 'react-i18next';
import { ESG_CONTENT_SHAPE } from '../../constants/colors';
import type { EsgFigureSize } from '../../constants/typography';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import { useKPIStore } from '../../stores/kpi.store';
import { findEnabledKpiLimit, type MetricPolarity } from '../../utils/metricPolarity';
import type { DeltaKind, MetricValueKind } from '../../utils/numberFormat';
import KpiFigure from '../metrics/KpiFigure';

interface StatCardProps {
  title: string;
  value: number | null;
  unit: string;
  trend: number;
  onMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
  /** When true, hide month-over-month trend (e.g. prior-month-only cards or overview). */
  hideTrend?: boolean;
  /** Optional leading icon (prefer an `ESG_ICONS` component). */
  icon?: SvgIconComponent | React.ReactNode;
  /** Optional second line under the main value (e.g. formatted cost). */
  subValue?: string;
  /** Period / context caption under the value (e.g. This Month, All-time). */
  subtitle?: string;
  /** Show the unit label even when value is null (displays "— unit"). */
  showUnitWhenEmpty?: boolean;
  /** Hide the overflow menu (read-only summary cards). */
  hideMenu?: boolean;
  /** Whether a rising value is good news (delta colour). Default: higher is better. */
  polarity?: MetricPolarity;
  /** `relative` (% change, default) or `points` (percentage-point change for rates). */
  deltaKind?: DeltaKind;
  /** Canonical metric key used to match KPI limits (language independent). */
  metricKey?: string;
  /** Formatting rule for the figure; inferred from the unit when omitted. */
  valueKind?: MetricValueKind;
  size?: EsgFigureSize;
  info?: React.ReactNode;
  /** Render without the outlined frame (when placed inside a MetricStrip). */
  frameless?: boolean;
}

function isIconComponent(icon: unknown): icon is SvgIconComponent {
  return typeof icon === 'function' || (typeof icon === 'object' && icon !== null && 'muiName' in icon);
}

/**
 * KPI cell used for dashboard metrics. Typography carries the hierarchy (no
 * hover lift, no shadow); delta colour follows the metric's polarity.
 */
const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  unit,
  trend,
  onMenuClick,
  hideTrend = false,
  icon,
  subValue,
  subtitle,
  showUnitWhenEmpty = false,
  hideMenu = false,
  polarity = 'higher_is_better',
  deltaKind = 'relative',
  metricKey,
  valueKind,
  size = 'md',
  info,
  frameless = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const fmt = useNumberFormat();
  const { limits } = useKPIStore();

  const kpiLimit =
    value !== null
      ? findEnabledKpiLimit(limits, metricKey) ??
        limits.find((limit) => limit.name === title.replace(/\s+/g, '_').toUpperCase() && limit.isEnabled)
      : undefined;

  const inferKind = (): MetricValueKind => {
    if (unit === '%' || unit === t('units.percent')) return 'percent';
    if (unit === 'tCO2e' || unit === 'tCO₂e' || unit === t('units.tco2e')) return 'tco2e';
    if (unit === 'hours' || unit === t('units.hours')) return 'hours';
    if (unit === 'SAR' || unit === t('units.sar')) return 'sar';
    if (unit === 'kg' || unit === t('units.kg')) return 'mass_kg';
    return 'decimal';
  };

  const kind = valueKind ?? inferKind();
  const figure = fmt.metric(kind, value);
  const showUnit = value !== null || showUnitWhenEmpty;

  const passesLimit = (v: number, limit: number, operator: string) => {
    switch (operator) {
      case '>':
      case '>=':
        return v >= limit;
      case '<':
      case '<=':
        return v <= limit;
      case '=':
        return Math.abs(v - limit) < 0.01;
      default:
        return true;
    }
  };

  const operatorSymbol: Record<string, string> = { '>': '>', '<': '<', '>=': '≥', '<=': '≤', '=': '=' };

  const menu = !hideMenu ? (
    <IconButton size="small" onClick={onMenuClick} aria-label={t('common.actions')} sx={{ color: 'text.secondary' }}>
      <MoreVertRounded fontSize="small" />
    </IconButton>
  ) : null;

  const body = (
    <KpiFigure
      label={title}
      icon={isIconComponent(icon) ? icon : React.isValidElement(icon) ? icon : undefined}
      value={figure.text}
      unit={showUnit ? unit : undefined}
      size={size}
      secondary={subValue || undefined}
      context={subtitle}
      info={info}
      action={menu}
      delta={
        !hideTrend && trend !== 0
          ? { value: trend, kind: deltaKind, polarity, comparisonLabel: t('dashboard.vsLastMonth') }
          : undefined
      }
    >
      {kpiLimit && value !== null ? (
        <Box sx={{ mt: 'auto', pt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
            <Typography variant="caption" color="text.secondary">
              {t('dashboard.kpiTarget')}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
              <bdi>
                {operatorSymbol[kpiLimit.operator] ?? kpiLimit.operator} {fmt.number(kpiLimit.value)} {unit}
              </bdi>
            </Typography>
          </Box>
          <Tooltip title={kpiLimit.alertMessage}>
            <LinearProgress
              variant="determinate"
              value={
                kpiLimit.operator === '='
                  ? passesLimit(value, kpiLimit.value, '=')
                    ? 100
                    : 0
                  : Math.min((value / (kpiLimit.value || 1)) * 100, 100)
              }
              sx={{
                '& .MuiLinearProgress-bar': {
                  backgroundColor: passesLimit(value, kpiLimit.value, kpiLimit.operator)
                    ? theme.palette.success.main
                    : theme.palette.error.main,
                },
              }}
            />
          </Tooltip>
        </Box>
      ) : null}
    </KpiFigure>
  );

  if (frameless) return body;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, md: 2.5 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: `${ESG_CONTENT_SHAPE.md}px`,
        bgcolor: 'background.paper',
      }}
    >
      {body}
    </Paper>
  );
};

export default StatCard;
